/**
 * HTML Renderer
 *
 * Pure renderer component for generic and artifact HTML content.
 *
 * This component only handles rendering - no header, no layout.
 * Parent wrapper provides the UI chrome.
 */

import { sanitizeAndHighlightArtifactHtml, sanitizeAndHighlightHtml } from '@/content-viewer/util/sanitize-and-highlight-html'
import { useEffect, useMemo, useRef } from 'react'
import { Parser } from 'html-to-react'

type HTMLBaseRendererProps = {
  htmlContent: string
  highlightSentences?: string[] // Array of sentences to highlight (from highlighted_citations)
  transformArtifactReferenceHtml?: (sanitizedHtml: string) => string
}

/**
 * HTMLRenderer Component
 *
 * Renders sanitized generic HTML, including artifact reference markers when provided.
 */
export default function HTMLBaseRenderer(props: HTMLBaseRendererProps): JSX.Element {
  const { htmlContent, highlightSentences, transformArtifactReferenceHtml } = props

  const containerRef = useRef<HTMLDivElement>(null)

  // Artifact citation transformers receive only sanitized markup. Cache by all
  // rendering inputs so unrelated parent renders don't rescan the document.
  const processedHtml = useMemo(() => {
    const sanitizedHtml = transformArtifactReferenceHtml
      ? sanitizeAndHighlightArtifactHtml(htmlContent || '', highlightSentences)
      : sanitizeAndHighlightHtml(htmlContent || '', highlightSentences)
    return transformArtifactReferenceHtml ? transformArtifactReferenceHtml(sanitizedHtml) : sanitizedHtml
  }, [htmlContent, highlightSentences, transformArtifactReferenceHtml])
  const hasHighlights = Boolean(highlightSentences && highlightSentences.length > 0)
  const parsedHtml = useMemo(() => Parser().parse(processedHtml), [processedHtml])

  // Scroll to first highlight if present
  useEffect(() => {
    // Only scroll if highlights exist
    if (!hasHighlights) return

    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      if (containerRef.current) {
        const firstHighlight = containerRef.current.querySelector('.ref-highlight')
        if (firstHighlight) {
          firstHighlight.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          })
        }
      }
    }, 250)

    return () => clearTimeout(timer)
  }, [hasHighlights, htmlContent, processedHtml])

  // Loading State
  return (
    <div className="relative h-full flex flex-col items-center p-3 animate-fadeIn">
      <div ref={containerRef} className="view-container gcs-md-highlights flex-grow border bg-white w-full">
        {parsedHtml}
      </div>
      <div>&nbsp;</div>
    </div>
  )
}
